import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usersAPI } from '../api/client';

/**
 * Single-select, search-that-closes-on-pick user picker -- modelled on
 * HierarchyNodeTreeSelect.js, the only existing component with that shape.
 * Flat rather than tree-indented (users have no hierarchy).
 *
 * Backed by GET /api/users/selectable, not /api/users -- any authenticated
 * user can populate this picker (e.g. a scoped author assigning a document
 * owner), where the full directory stays behind `users.read`. See that
 * endpoint's docstring in routers/users.py.
 *
 * `options` overrides that fetch for a caller that has already loaded (and
 * narrowed) a list of its own -- GroupMembersPanel, which must exclude people
 * who are already active members of the committee. Rows need only the four
 * fields UserSelectableResponse carries; a UserResponse satisfies that too.
 */
function UserSelect({ value, onChange, placeholder = 'No owner', disabled, options = null }) {
  const [fetched, setFetched] = useState([]);
  const [loading, setLoading] = useState(options === null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (options !== null) return undefined;
    let cancelled = false;
    usersAPI.selectable()
      .then((res) => { if (!cancelled) setFetched(res.data); })
      .catch((err) => console.error('Error loading selectable users:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [options]);

  const users = options === null ? fetched : options;

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const optionLabel = (u) => `${u.last_name}, ${u.first_name} — ${u.email}`;

  const trimmedQuery = query.trim().toLowerCase();
  const visible = useMemo(() => {
    if (!trimmedQuery) return users;
    return users.filter((u) => optionLabel(u).toLowerCase().includes(trimmedQuery));
  }, [users, trimmedQuery]);

  const select = (userId) => {
    onChange(userId);
    setOpen(false);
    setQuery('');
  };

  if (loading) {
    return <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Loading users...</div>;
  }

  const selectedUser = users.find((u) => u.id === value);
  const label = selectedUser ? optionLabel(selectedUser) : placeholder;

  return (
    <div className="multiselect" ref={ref}>
      <button
        type="button"
        className="multiselect-toggle"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="multiselect-toggle-label">{label}</span>
        <span className="multiselect-toggle-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="multiselect-panel">
          <input
            type="text"
            className="multiselect-search"
            placeholder="Filter by name or email..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="multiselect-options" role="listbox">
            <label className="multiselect-option">
              <input type="radio" checked={!value} onChange={() => select(null)} />
              <span>{placeholder}</span>
            </label>
            {visible.map((u) => (
              <label key={u.id} className="multiselect-option">
                <input type="radio" checked={value === u.id} onChange={() => select(u.id)} />
                <span>{optionLabel(u)}</span>
              </label>
            ))}
            {visible.length === 0 && (
              <div style={{ fontSize: '13px', color: 'var(--muted)', padding: '6px 8px' }}>No matches.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default UserSelect;
